import { makeAutoObservable, runInAction } from "mobx";
import { AxiosError } from "axios";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type {
  ILmsCatalogQuery,
  ILmsLinkError,
  TLmsCatalogEntry,
  TLmsCourseLink,
  TLmsKeptContent,
  TLmsLinkErrorCode,
  TLmsLinkPreview,
} from "@/types/lms";

/** The id an optimistic link carries until the server hands back a real one.
 *
 *  Exported so a caller can tell the row apart — it is the one row whose unlink button
 *  has nothing to DELETE yet.
 */
export const OPTIMISTIC_LINK_ID = "__optimistic_link__";

/** The four facts a link write needs, whichever surface supplies them.
 *
 *  Declared rather than taking a `TLmsCatalogEntry`, because the picker is no longer the
 *  only place a link starts: a kept row on Delivery carries the same four and none of
 *  the rest of a catalog entry. A structural type keeps both honest — the alternative
 *  was a cast, which is a promise the compiler stops checking.
 */
export interface ILmsLinkTarget {
  connection: string;
  kind: TLmsCatalogEntry["kind"];
  external_id: string;
  name: string;
}

/** A project's links to catalogued LMS content, and the picker's view of the catalog.
 *
 *  **Optimistic on both writes**, which is the design-system default rather than an
 *  exception, and worth stating because the sibling connection store is deliberately
 *  the opposite. Nothing here calls the vendor: linking writes one Cognota-side row
 *  validated against a cache we already hold, and unlinking soft-deletes it and removes
 *  nothing that was ever synced. So neither result is unknowable client-side and neither
 *  is irreversible — the two conditions that would justify making the user wait.
 */
export class ProjectLmsLinkStore {
  /** Keyed by project id: this surface is reachable from several projects in one
   *  session, and a single array would show the previous project's links for the frame
   *  before the fetch resolves. */
  linksByProject: Record<string, TLmsCourseLink[]> = {};
  isLoadingLinks = false;

  /** What the requests behind each project kept (NG-1157). Keyed by project for the
   *  reason the links are: this surface is reachable from several projects in a session.
   */
  keptByProject: Record<string, TLmsKeptContent[]> = {};
  isLoadingKept = false;

  /** The Delivered-by row's Preview facts, per link id (NG-1858). */
  linkPreviewById: Record<string, TLmsLinkPreview> = {};
  /** Link ids whose preview fetch is in flight, so the rail can show a skeleton. */
  linkPreviewLoadingIds: Record<string, true> = {};

  catalog: TLmsCatalogEntry[] = [];
  catalogHasMore = false;
  catalogTotalCount = 0;
  /** False when the workspace has no *active* connection — which is a different empty
   *  than "nothing matched", and needs different words. */
  catalogHasActiveConnection = true;
  isLoadingCatalog = false;

  private rootStore: RootStore;
  /** Latest-wins for the picker's typeahead: a slow early request must never overwrite
   *  the results of a later, narrower one. */
  private catalogRequestSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  linksFor(projectId: string): TLmsCourseLink[] {
    return this.linksByProject[projectId] ?? [];
  }

  keptFor(projectId: string): TLmsKeptContent[] {
    return this.keptByProject[projectId] ?? [];
  }

  linkPreviewFor(linkId: string): TLmsLinkPreview | null {
    return this.linkPreviewById[linkId] ?? null;
  }

  isLinkPreviewLoading(linkId: string): boolean {
    return Boolean(this.linkPreviewLoadingIds[linkId]);
  }

  /** Load the Delivered-by row's Preview facts (NG-1858), once per link per session.
   *
   *  A failure is a degradation, not a broken action: the dialog still shows what the
   *  row carried, and only the stored-facts sections go missing, so it warns and a later
   *  open tries again. Keyed by link id: a re-link is a new row with a new id, so a
   *  cached answer can never describe a different link.
   */
  async fetchLinkPreview(slug: string, projectId: string, linkId: string): Promise<void> {
    if (this.linkPreviewById[linkId] || this.linkPreviewLoadingIds[linkId]) return;
    runInAction(() => {
      this.linkPreviewLoadingIds[linkId] = true;
    });
    try {
      const preview = await this.rootStore.lmsLinkService.getLinkPreview(slug, projectId, linkId);
      if (preview) {
        runInAction(() => {
          this.linkPreviewById[linkId] = preview;
        });
      }
    } catch (error) {
      console.warn("[lms] link preview fetch failed", { slug, projectId, linkId, error });
    } finally {
      runInAction(() => {
        delete this.linkPreviewLoadingIds[linkId];
      });
    }
  }

  /** The kept candidates, read once per visit.
   *
   *  A failed read leaves the list empty and is logged rather than thrown: this is a
   *  secondary section under "Delivered by", and taking the Delivery page down because
   *  the request-side read failed would cost somebody the link they came to make.
   */
  async fetchKeptContent(slug: string, projectId: string): Promise<void> {
    this.isLoadingKept = true;
    try {
      const kept = await this.rootStore.lmsLinkService.listKeptContent(slug, projectId);
      runInAction(() => {
        this.keptByProject[projectId] = kept;
      });
    } catch (error) {
      console.error("[lms] kept content fetch failed", { slug, projectId, error });
      runInAction(() => {
        this.keptByProject[projectId] = [];
      });
    } finally {
      runInAction(() => {
        this.isLoadingKept = false;
      });
    }
  }

  /** Make one kept item the project's Delivered by.
   *
   *  Nothing more than `link` with the row's own triple: it is the same write the picker
   *  makes, so it lands on screen at once and carries the same audit trail.
   *
   *  **The kept rows are re-read by `link` itself, not here**, and that placement is the
   *  fix for a real defect rather than tidiness. Which rows become unavailable is the
   *  one-link-per-connection rule, so it is re-read rather than re-derived in the
   *  browser — but a link made from the *picker* invalidates those rows exactly as much
   *  as one made from a kept row. With the re-read living on this method alone, the
   *  picker's link left a kept row still offering to become the link it already was.
   */
  async linkKeptContent(
    slug: string,
    projectId: string,
    kept: TLmsKeptContent,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.link(
      slug,
      projectId,
      {
        connection: kept.connection_id,
        kind: kept.kind,
        external_id: kept.external_id,
        // A kept row whose catalog entry has gone renders the vendor id as its name, and
        // the link write below is refused by the API anyway (`catalog_entry_not_found`) —
        // so this only ever labels the optimistic row for the instant before that.
        name: kept.name ?? kept.external_id,
      },
      source,
      "kept",
    );
  }

  /** Take one kept item off this project's list, on screen first (NG-1891).
   *
   *  Optimistic for the reason both link writes are: it writes one Cognota-side row and
   *  calls no vendor, so the result is knowable client-side, and undoing it is putting
   *  the row back. Every row for the item goes, whichever request kept it, because that
   *  is what the server hides.
   *
   *  **It does not touch the request's Keep**, which is why nothing else is re-read: the
   *  request panel, the Requests count and Plan work all still show the item.
   */
  async dismissKeptContent(
    slug: string,
    projectId: string,
    kept: TLmsKeptContent,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const before = this.keptFor(projectId);
    const same = (row: TLmsKeptContent) =>
      row.connection_id === kept.connection_id &&
      row.kind === kept.kind &&
      row.external_id === kept.external_id;
    runInAction(() => {
      this.keptByProject[projectId] = before.filter((row) => !same(row));
    });
    try {
      await this.rootStore.lmsLinkService.dismissKeptContent(slug, projectId, {
        connection_id: kept.connection_id,
        kind: kept.kind,
        external_id: kept.external_id,
      });
      track("lms_kept_content_dismissed", {
        kind: kept.kind ?? "course",
        project_id: projectId,
        connection_id: kept.connection_id,
        source,
      });
    } catch (error) {
      console.error("[lms] dismiss kept content failed", {
        slug,
        projectId,
        connection: kept.connection_id,
        kind: kept.kind,
        external_id: kept.external_id,
        error,
      });
      runInAction(() => {
        this.keptByProject[projectId] = before;
      });
      throw error;
    }
  }

  async fetchLinks(slug: string, projectId: string): Promise<TLmsCourseLink[]> {
    this.isLoadingLinks = true;
    try {
      const links = await this.rootStore.lmsLinkService.listLinks(slug, projectId);
      runInAction(() => {
        this.linksByProject[projectId] = links;
      });
      return links;
    } finally {
      runInAction(() => {
        this.isLoadingLinks = false;
      });
    }
  }

  async fetchCatalog(slug: string, projectId: string, query: ILmsCatalogQuery = {}): Promise<void> {
    const seq = ++this.catalogRequestSeq;
    this.isLoadingCatalog = true;
    try {
      const page = await this.rootStore.lmsLinkService.listCatalog(slug, projectId, query);
      if (seq !== this.catalogRequestSeq) return;
      runInAction(() => {
        this.catalog = page.entries;
        this.catalogHasMore = page.hasMore;
        this.catalogTotalCount = page.totalCount;
        this.catalogHasActiveConnection = page.hasActiveConnection;
      });
    } catch (error) {
      console.error("[lms] catalog fetch failed", { slug, projectId, query, error });
      if (seq === this.catalogRequestSeq) {
        runInAction(() => {
          this.catalog = [];
          this.catalogHasMore = false;
          this.catalogTotalCount = 0;
        });
      }
      throw error;
    } finally {
      if (seq === this.catalogRequestSeq) {
        runInAction(() => {
          this.isLoadingCatalog = false;
        });
      }
    }
  }

  /** Link this project to a catalog entry, on screen first.
   *
   *  The optimistic row is built from the entry the user just clicked, so the name and
   *  kind on screen are the ones they picked rather than a guess. Only the id is
   *  server-assigned, and nothing renders it until the POST answers.
   */
  async link(
    slug: string,
    projectId: string,
    entry: ILmsLinkTarget,
    source: AnalyticsSource = "page",
    // Where the entry was picked: the catalog picker, or a request's kept content.
    origin: "catalog" | "kept" = "catalog",
  ): Promise<TLmsCourseLink> {
    const before = this.linksFor(projectId);
    const optimistic: TLmsCourseLink = {
      id: OPTIMISTIC_LINK_ID,
      connection: entry.connection,
      // Not a guess: the catalog endpoint offers entries from *active* connections only,
      // so anything pickable is by definition live. Leaving it undefined would flash a
      // "Not connected" badge (the honest fallback everywhere else) on a link that is
      // fine, for as long as the POST takes.
      connection_state: "active",
      project: projectId,
      kind: entry.kind,
      external_id: entry.external_id,
      name: entry.name,
    };
    runInAction(() => {
      this.linksByProject[projectId] = [optimistic, ...before];
    });

    try {
      const link = await this.rootStore.lmsLinkService.linkProject(slug, projectId, {
        connection: entry.connection,
        kind: entry.kind,
        external_id: entry.external_id,
      });
      runInAction(() => {
        this.linksByProject[projectId] = [
          link,
          ...this.linksFor(projectId).filter((row) => row.id !== OPTIMISTIC_LINK_ID),
        ];
      });
      track("lms_project_linked", {
        kind: entry.kind ?? "course",
        project_id: projectId,
        link_id: link.id,
        connection_id: entry.connection,
        origin,
        source,
      });
      // The kept rows describe THIS project's link state — which one is already
      // Delivered by, and which are blocked by the one-link-per-connection rule — so a
      // link made anywhere invalidates them, not just one made from a kept row. Missing
      // this left the picker's own link showing a kept row still offering to become the
      // link it already was, and its siblings enabled into a guaranteed 409.
      //
      // Never allowed to fail the write: the link is committed by the time this runs,
      // and `fetchKeptContent` logs and empties rather than throwing, so a stale section
      // is the worst case rather than a successful link reported as an error.
      await this.fetchKeptContent(slug, projectId);
      return link;
    } catch (error) {
      // The ids are the whole of triage here: which project, and which vendor object.
      console.error("[lms] link project failed", {
        slug,
        projectId,
        connection: entry.connection,
        kind: entry.kind,
        external_id: entry.external_id,
        error,
      });
      runInAction(() => {
        this.linksByProject[projectId] = before;
      });
      throw toLinkError(error);
    }
  }

  /** Unlink, on screen first.
   *
   *  Reversible by re-linking the same entry — the uniqueness constraint reads
   *  `deleted_at IS NULL`, so the slot genuinely frees — which is why this does not
   *  block on the server the way disconnecting a connection does.
   */
  async unlink(
    slug: string,
    projectId: string,
    linkId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const before = this.linksFor(projectId);
    runInAction(() => {
      this.linksByProject[projectId] = before.filter((row) => row.id !== linkId);
    });
    try {
      await this.rootStore.lmsLinkService.unlinkProject(slug, projectId, linkId);
      track("lms_project_unlinked", { project_id: projectId, link_id: linkId, source });
      // The other direction of the same staleness: unlinking frees the connection's one
      // slot, so every kept row blocked on it has to stop saying it is blocked.
      await this.fetchKeptContent(slug, projectId);
    } catch (error) {
      console.error("[lms] unlink project failed", { slug, projectId, linkId, error });
      runInAction(() => {
        this.linksByProject[projectId] = before;
      });
      throw error;
    }
  }

  reset(): void {
    this.linksByProject = {};
    this.keptByProject = {};
    this.isLoadingKept = false;
    this.linkPreviewById = {};
    this.linkPreviewLoadingIds = {};
    this.catalog = [];
    this.catalogHasMore = false;
    this.catalogTotalCount = 0;
    this.catalogHasActiveConnection = true;
    this.isLoadingLinks = false;
    this.isLoadingCatalog = false;
  }
}

const KNOWN_CODES: TLmsLinkErrorCode[] = [
  "connection_not_connected",
  "catalog_entry_not_found",
  "project_already_linked",
];

/** Turn a refused link into something the surface can say out loud.
 *
 *  An unrecognised failure keeps a null code rather than being mapped onto the nearest
 *  known one: a confident wrong diagnosis costs more than an honest "could not link".
 */
export function toLinkError(error: unknown): ILmsLinkError {
  const data = (
    error as AxiosError<{ error?: string; code?: string; existing_external_id?: string }>
  )?.response?.data;
  const code = KNOWN_CODES.find((known) => known === data?.code) ?? null;
  return {
    code,
    message: data?.error ?? (error instanceof Error ? error.message : String(error)),
    existingExternalId: data?.existing_external_id ?? null,
  };
}
